import { Company } from '@models/Company'
import { companySchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createCompany as createCompanyAPI,
  deleteCompany as deleteCompanyAPI,
  fetchCompanies,
  fetchCompanyById,
  updateCompany as updateCompanyAPI,
} from '@services/CompanyService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Company> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'company',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCompaniesSuccess,
  setQuery: setCompanyQuery,
  reset: resetCompanyState,
  resetQueryAndIds: resetCompanyQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Company }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCompanies = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCompany = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCompanyById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCompaniesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCompany =
  (company: Company | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Company>(company)))

export const getCompanies = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCompanies(params),
    [companySchema],
    slice.actions
  )

export const createCompany = (company: Company) =>
  SBAPI.CreateDispatched(
    createCompanyAPI(company),
    companySchema,
    slice.actions
  )

export const getCompanyWithId = (id: number) =>
  SBAPI.FetchDispatched(fetchCompanyById(id), companySchema, slice.actions)

export const updateCompany = (company: Company) =>
  SBAPI.UpdateDispatched<Company>(
    updateCompanyAPI(company),
    company,
    slice.actions
  )

export const deleteCompany = (company: Company) =>
  SBAPI.DestroyDispatched<Company>(
    deleteCompanyAPI(company),
    company,
    slice.actions
  )
